import moment, { Moment } from 'moment';
import { ResourceAllocationApiDateRange } from 'api/resource-management/types';
import {
  format,
  formatterWithMinutesAndSeconds,
} from 'components/atoms/input-duration/formatter';
import { formatTimeWithSeconds } from 'components/organisms/timesheets/common/format-time';
import {
  isNonWorkingDay,
  numberOfWorkingDaysBetweenDates,
} from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity/types';
import { defaultFormat } from 'utils/date-field/formatter';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import { Range } from '../types';

export const getDateAfter = (
  startDate: Moment,
  workingDaysConfiguration: WorkingDaysConfiguration,
  numberOfDays: number,
): Moment => {
  let endDate = startDate.add(numberOfDays - 1, 'days').endOf('day');

  while (true) {
    const numberOfWorkingDays = numberOfWorkingDaysBetweenDates(
      startDate,
      endDate,
      workingDaysConfiguration,
    );

    if (numberOfDays === numberOfWorkingDays) {
      break;
    }
    endDate = endDate.add(Math.abs(numberOfDays - numberOfWorkingDays), 'days');
  }

  return endDate;
};

export const shiftDateIfOnNonWorkingDay = (
  date: Moment,
  workingDaysConfiguration: WorkingDaysConfiguration,
  forward = true,
): Moment => {
  let tDate = date;

  while (isNonWorkingDay(tDate, workingDaysConfiguration)) {
    tDate = tDate.add(forward ? 1 : -1, 'day');
  }

  return tDate;
};

export const startDateParser = (date: Date | string): Moment =>
  moment(date).startOf('day');
export const endDateParser = (date: Date | string): Moment =>
  moment(date).endOf('day');

export const isDateBetweenRange = (
  date: Moment,
  { startDate, endDate }: { startDate: Moment; endDate: Moment },
): boolean => date.isBetween(startDate, endDate, 'day', '[]');

export const ignoreMinutesIfZero = (minutes: number): string =>
  minutes % 60 === 0
    ? format('hh', minutes / 60)
    : formatTimeWithSeconds(minutes);
export const formatTimeWithoutIgnoringNonZeroMinutes = (
  minutes: number,
): string =>
  minutes < 0
    ? `-${formatterWithMinutesAndSeconds(Math.abs(minutes))}`
    : formatterWithMinutesAndSeconds(minutes);
export const removeInsignificantTrailingZero = (
  number: number | string | undefined,
): string => {
  if (number == null || number === '') {
    return '0';
  }
  if (typeof number === 'string' && number.charAt(number.length - 1) === '.') {
    return number;
  }

  return parseFloat(parseFloat(number.toString()).toFixed(1)).toString();
};

export const expandRangeToIncludeNonWorkingDays = (
  { fromDate, toDate }: Range,
  workingDaysConfig: WorkingDaysConfiguration,
): Range => {
  let resultFromDate = fromDate;
  let resultToDate = toDate;

  if (isNonWorkingDay(fromDate.subtract(1, 'day'), workingDaysConfig)) {
    resultFromDate = shiftDateIfOnNonWorkingDay(
      fromDate.subtract(1, 'day'),
      workingDaysConfig,
      false,
    ).add(1, 'day');
  }
  if (isNonWorkingDay(toDate.add(1, 'day'), workingDaysConfig)) {
    resultToDate = shiftDateIfOnNonWorkingDay(
      toDate.add(1, 'day'),
      workingDaysConfig,
    ).subtract(1, 'day');
  }

  return {
    fromDate: resultFromDate,
    toDate: resultToDate,
  };
};

export const getCorrectedDateRangeBasedOnView = (
  range: Range,
  currentViewPreset: ViewPresetId,
  workingDaysConfig: WorkingDaysConfiguration,
): Range => {
  const correctedRange: Range = {} as Range;

  switch (currentViewPreset) {
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      correctedRange.fromDate = range.fromDate.startOf('day');
      correctedRange.toDate = range.toDate.endOf('day');
      break;
    case ViewPresetId.Month:
    case ViewPresetId.MonthZoomed:
      correctedRange.fromDate = range.fromDate.startOf('month');
      correctedRange.toDate = range.toDate.endOf('month');
      break;
    case ViewPresetId.Week:
    case ViewPresetId.WeekZoomed:
    default:
      correctedRange.fromDate = range.fromDate.startOf('week').add(1, 'day'); // this is to start week with Monday
      correctedRange.toDate = range.toDate.endOf('week').add(1, 'days'); // this is to end week on Sunday

      break;
  }

  return {
    fromDate: shiftDateIfOnNonWorkingDay(
      correctedRange.fromDate,
      workingDaysConfig,
    ),
    toDate: shiftDateIfOnNonWorkingDay(
      correctedRange.toDate,
      workingDaysConfig,
      false,
    ),
  };
};

export const getCorrectedApiDateRangeBasedOnView = (
  range: Range,
  currentViewPreset: ViewPresetId,
  workingDaysConfig: WorkingDaysConfiguration,
): ResourceAllocationApiDateRange => {
  const correctedRange = getCorrectedDateRangeBasedOnView(
    range,
    currentViewPreset,
    workingDaysConfig,
  );

  return {
    startDate: defaultFormat(correctedRange.fromDate),
    endDate: defaultFormat(correctedRange.toDate),
  };
};
